import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { Loader2, FileDown, ArrowLeft } from "lucide-react";
import { generatePDF } from "src/utils/pdfGenerator";
import purchaseOrderApis from "src/api/purchaseOrder";
import Card from "src/components/common/card/Card";

const PurchaseOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const orderDetailsRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (orderId) getOrderDetails(orderId);
  }, [orderId]);

  const getOrderDetails = async (orderId: string) => {
    try {
      setLoading(true);
      const res = await purchaseOrderApis.getDetails(orderId);
      setOrder(res?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setInvoiceLoading(true);
      await generatePDF(
        orderDetailsRef,
        `invoice-${order.purchaseOrderId}.pdf`
      );
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate invoice");
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-indigo-600" size={28} />
          <span className="ml-3 text-gray-700">Loading order...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-gray-500">
          Order not found or deleted.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      meta={{
        title: `Order ${order.purchaseOrderId} | Invyntra Dashboard`,
        description: "View full sale order details and generate invoices.",
        keywords: "sales order details, invoice, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800 flex items-center text-sm"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </button>
          </div>

          <Breadcrumb
            data={[
              { label: "Home", path: "/app/dashboard" },
              { label: "Purchases", path: "/app/purchase/list" },
              {
                label: `#${order.purchaseOrderId}`,
                path: `/app/purchase/${orderId}`,
              },
            ]}
          />
        </div>

        <div ref={orderDetailsRef} className="pdf-container space-y-6 pb-4">
          <h1 className="text-xl font-semibold text-gray-800">
            Order #{order.purchaseOrderId}
          </h1>

          {/* Supplier Info */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Supplier Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <p>
                <span className="font-medium">Name: </span>
                {order.supplierId?.name} ({order.supplierId.supplierId})
              </p>
              <p>
                <span className="font-medium">Mobile: </span>
                {order.supplierId?.phone}
              </p>
              <p>
                <span className="font-medium">Email: </span>
                {order.supplierId?.email || "-"}
              </p>
              <p>
                <span className="font-medium">Address: </span>
                {order.supplierId?.address || "-"}
              </p>
            </div>
          </Card>

          {/* Products */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Ordered Products
            </h3>

            <TableV2
              columns={[
                {
                  key: "productId.name",
                  header: "Product",
                  accessor: ({ row }) => row.productId?.name,
                },
                { key: "quantity", header: "Qty" },
                {
                  key: "unitPrice",
                  header: "Price",
                  accessor: ({ row }) =>
                    currencyFormatter(row.unitPrice.toFixed(2), "IN", 2),
                },
                {
                  key: "taxPercent",
                  header: "Tax (%)",
                },
                {
                  key: "totalPrice",
                  header: "Total",
                  accessor: ({ row }) =>
                    currencyFormatter(row.totalPrice.toFixed(2), "IN", 2),
                },
              ]}
              data={order.products || []}
            />
          </Card>

          {/* Payment Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Payment Summary
            </h3>

            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <p>
                <span className="font-medium">Subtotal:</span>{" "}
                {currencyFormatter(order.subTotal, "IN", 2)}
              </p>
              <p>
                <span className="font-medium">Tax:</span>{" "}
                {currencyFormatter(order.taxTotal, "IN", 2)}
              </p>
              <p>
                <span className="font-medium">Discount:</span>{" "}
                {currencyFormatter(order.discount, "IN", 2)}
              </p>
              <p>
                <span className="font-medium">Grand Total:</span>{" "}
                {currencyFormatter(order.grandTotal, "IN", 2)}
              </p>
              <p>
                <span className="font-medium">Order Status:</span>{" "}
                {order.orderStatus?.toUpperCase()}
              </p>
              <p>
                <span className="font-medium">Payment Status:</span>{" "}
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    order.paymentStatus === "paid"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {order.paymentStatus}
                </span>
              </p>
            </div>
          </Card>

          {/* Meta Info + Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p>
                <span className="font-medium">Created By:</span>{" "}
                {order.createdBy?.firstName || "Staff"}
              </p>
              <p>
                <span className="font-medium">Created At:</span>{" "}
                {formatDateTime(order.createdAt, "DD/MM/YYYY hh:mm A")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end !-mt-14">
          <Button
            onClick={handleGenerateInvoice}
            className="bg-indigo-600 text-white flex items-center"
            disabled={invoiceLoading}
          >
            {invoiceLoading ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />{" "}
                Generating...
              </>
            ) : (
              <>
                <FileDown size={16} className="mr-2" /> Generate Invoice
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PurchaseOrderDetailsPage;
